import React, { useEffect } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { aiGatewayService } from '@/services/aiGateway';
import {
  selectActiveSynthJobs,
  useSynthJobsStore,
} from '@/stores/useSynthJobsStore';
import { useTranslation } from '@/hooks/useTranslation';
import { clearCompletionIntent } from '@/lib/completionIntent';
import { medicalRecordsKeys } from '@/hooks/useMedicalRecords';
import { noteRoute } from '@/config/noteRoute';

/**
 * Mounted at the app shell. Bridges global scribe.* socket events into
 * the `useSynthJobsStore`. Without this, the store would only update
 * while the dock or review page is mounted — backgrounding a synth
 * (the whole point of the "Continue in background" flow) would freeze
 * the pill on whatever stage was last visible.
 *
 * The bridge does NOT own job creation — pages register their own jobs
 * via `register(...)` then trust the bridge to take over. The bridge
 * just owns the scribe.* → store mapping for the lifetime of every
 * registered job, regardless of which route the doctor is on.
 */

const friendlyStatus = (raw: string | undefined, isRTL: boolean): string | null => {
  if (!raw) return null;
  const lower = raw.toLowerCase();
  if (lower.includes('finaliz')) {
    return isRTL ? 'جمع كل المحادثة…' : 'Pulling the full conversation together…';
  }
  if (lower.includes('transcrib')) {
    return isRTL ? 'تفريغ الصوت إلى نص…' : 'Turning the recording into text…';
  }
  if (lower.includes('soap') || lower.includes('generat')) {
    return isRTL ? 'تجهيز ملاحظة الزيارة…' : 'Drafting your encounter note…';
  }
  return raw;
};

export const SynthEventsBridge: React.FC = () => {
  const { isRTL } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    const findJobBySession = (incomingSessionId: string | undefined | null) => {
      const jobs = selectActiveSynthJobs(useSynthJobsStore.getState());
      // Prefer an exact session match. Fall back to the only running job
      // when the event is session-less (regenerate path) and there's
      // exactly one — ambiguous otherwise (two regenerates in flight is
      // a corner case we'll just no-op on).
      if (incomingSessionId) {
        const exact = jobs.find((j) => j.sessionId === incomingSessionId);
        if (exact) return exact;
      }
      const sessionless = jobs.filter((j) => j.sessionId === null);
      if (sessionless.length === 1) return sessionless[0];
      return null;
    };

    const offProcessing = aiGatewayService.on(
      'scribe.processing',
      (data: { message?: string; sessionId?: string }) => {
        const job = findJobBySession(data?.sessionId);
        if (!job) return;
        const label = friendlyStatus(data?.message, isRTL);
        if (label) useSynthJobsStore.getState().setStatusLabel(job.id, label);
      },
    );

    const offUpdate = aiGatewayService.on(
      'scribe.processing-update',
      (data: { status?: string; sessionId?: string }) => {
        const job = findJobBySession(data?.sessionId);
        if (!job) return;
        if (data?.status === 'generating') {
          useSynthJobsStore
            .getState()
            .setStatusLabel(
              job.id,
              isRTL ? 'تجهيز ملاحظة الزيارة…' : 'Drafting your encounter note…',
            );
        }
      },
    );

    const offStage = aiGatewayService.on(
      'scribe.validation-stage',
      (data: {
        id?: string;
        status?: 'pending' | 'active' | 'done' | 'failed';
        detail?: string;
        sessionId?: string;
      }) => {
        const job = findJobBySession(data?.sessionId);
        if (!job || !data?.id || !data?.status) return;
        useSynthJobsStore.getState().setStage(job.id, {
          id: data.id,
          status: data.status,
          detail: data.detail,
        });
      },
    );

    const offResult = aiGatewayService.on(
      'scribe.soap-result',
      (data: {
        sessionId?: string;
        medicalRecordId?: string;
        recordId?: string;
        patientId?: string;
      }) => {
        const job = findJobBySession(data?.sessionId);
        if (!job) return;
        const recordId = data.medicalRecordId ?? data.recordId ?? null;
        useSynthJobsStore.getState().complete(job.id, {
          recordId,
          patientId: data.patientId ?? job.patientId ?? null,
        });
        // Single owner of completionIntent cleanup on success — the
        // dock used to do this in its per-request callback, but the
        // attach-to-patient flow navigates away before that runs and
        // the socket event lands here instead. Centralizing it means
        // every code path (dock, attach, recovery) clears the same way.
        if (job.sessionId) clearCompletionIntent(job.sessionId);
        // Refresh any medical-records list/detail caches the doctor is
        // currently looking at so the new note shows up immediately
        // without a manual refresh — the patient chart, notes page,
        // and review page are all driven by these keys.
        queryClient.invalidateQueries({ queryKey: medicalRecordsKeys.all });
        // Clinician-friendly success toast with a one-click open.
        // "Review now" instead of "Open" because the destination is the
        // edit-then-sign surface — the doctor's actual next action when
        // the synth wraps up while they're already mid-flow somewhere
        // else. Non-intrusive (default sonner placement, 8s).
        toast.success(
          isRTL
            ? `جاهزة: ملاحظة ${job.patientName}`
            : `Ready: ${job.patientName}'s note`,
          {
            action: recordId
              ? {
                  label: isRTL ? 'راجع الآن' : 'Review now',
                  onClick: () => navigate(noteRoute(recordId)),
                }
              : undefined,
            duration: 8000,
          },
        );
      },
    );

    const offError = aiGatewayService.on(
      'scribe.error',
      (data: { message?: string; sessionId?: string }) => {
        // scribe.error sometimes carries no sessionId; if a unique
        // session-less job is the only candidate we still flag it.
        const job = findJobBySession(data?.sessionId);
        if (!job) return;
        const msg =
          data?.message ?? (isRTL ? 'فشل التوليد' : 'Generation failed');
        useSynthJobsStore.getState().fail(job.id, msg);
        toast.error(
          isRTL
            ? `تعذّر إنشاء ملاحظة ${job.patientName}: ${msg}`
            : `Couldn't generate ${job.patientName}'s note: ${msg}`,
          { duration: 10000 },
        );
        // Terminal failure from the FE's perspective — clear the
        // intent so the recovery driver doesn't keep retrying. The
        // backend auto-retry cron handles server-side retries; the
        // doctor can still manually re-fire from PendingRecordingsCard.
        if (job.sessionId) clearCompletionIntent(job.sessionId);
      },
    );

    // Post-process pass landed — swap the transcript shown on the
    // synth overlay / pill to the refined version. The toast nudges
    // the doctor that a higher-quality transcript is available; on
    // the review page, hydrate-from-server picks up the persisted
    // refined_transcript and offers the regenerate affordance.
    const offRefined = aiGatewayService.on(
      'scribe.transcript-refined',
      (data: {
        sessionId?: string;
        recordingSessionId?: string;
        refinedTranscript?: string;
        elapsedMs?: number;
      }) => {
        const job = findJobBySession(data?.sessionId);
        if (!job) return;
        if (data?.refinedTranscript) {
          useSynthJobsStore
            .getState()
            .setTranscript(job.id, data.refinedTranscript, true);
        }
        // Quiet info toast — no action needed from the doctor unless
        // they want to regenerate. We don't show the "Open" button
        // here because the SOAP path is still in flight (or just
        // finished); the success toast for that already has it.
        toast(
          isRTL
            ? `نص محسّن جاهز لـ ${job.patientName}`
            : `Refined transcript ready for ${job.patientName}`,
          { duration: 4000 },
        );
      },
    );

    return () => {
      offProcessing();
      offUpdate();
      offStage();
      offResult();
      offError();
      offRefined();
    };
  }, [isRTL, navigate, queryClient]);

  return null;
};
